<template>
  <div>
    <b-overlay
      :show="recipesResponses('recipesList') && recipesEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Recipes
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Settings</b-breadcrumb-item>
            <b-breadcrumb-item active>Recipes</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
        <div class="ml-auto">
          <b-button
            size="sm"
            v-wave
            variant="primary"
            class="align-middle btn-shadow"
            :disabled="recipesResponses('manageRecipe')"
            @click="showModal('manageRecipeModal')">
            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">Add New Recipe</span>
          </b-button>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">

        <b-card-body>
          <b-table
            :items="recipesList"
            :fields="recipesFields"
            :class="{ 'table-hover' : !recipesEmpty }"
            stacked="lg"
            head-variant=""
            show-empty
            responsive
            class="mb-0">
            <template #cell(order)="data">
              <span class="small font-weight-light">{{ data.index + 1 }}</span>
            </template>
            <template #cell(name)="data">
              <div class="d-flex align-items-center">
                <span
                  class="d-inline-block text-truncate"
                  style="max-width: 170px;">{{ data.item.name }}</span>
                <span
                  v-if="recipeHasDescription(data.item._id)"
                  v-b-tooltip.hover
                  :title="data.item.description"
                  class="label label-sm ml-3 cursor-help">
                  <i class="fas fa-question fa-xs"></i>
                </span>
              </div>
            </template>
            <template #cell(slug)="data">
              <span
                class="d-inline-block text-truncate"
                style="max-width: 180px;">{{ data.item.slug }}</span>
            </template>
            <template #cell(tags)="data">
              <div
                v-if="!recipeTagsEmpty(data.item._id)"
                class="d-flex flex-wrap w-100 pt-2">
                <b-badge
                  v-for="tag in data.item.tags"
                  :key="`tag-${tag}-${data.item._id}`"
                  variant="light-info"
                  class="mr-2 mb-2">{{ tag }}</b-badge>
              </div>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(groups)="data">
              <ul
                v-if="!recipeGroupsEmpty(data.item._id)"
                class="list-unstyled mb-0">
                <li
                  v-for="group in data.item.groups"
                  :key="group._id"
                  class="my-2">
                  <i class="fas fa-folder-open fa-xs"></i>
                  {{ group.name }}
                </li>
              </ul>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(version)="data">
              <div v-if="data.item.version">
                <span class="font-size-xs">v</span>
                <span>{{ data.item.version }}</span>
              </div>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(edit)="data">
              <div class="d-flex flex-wrap flex-sm-nowrap justify-content-lg-end">
                <b-button
                  size="sm"
                  v-wave
                  variant="outline-primary"
                  class="mr-4 align-middle"
                  @click="
                    data.toggleDetails(),
                    getRecipeVersions(data.item._id, data.detailsShowing)
                  ">
                  <i
                    class="fas fa-sm"
                    :class="data.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'" />
                  <span v-if="!data.detailsShowing">
                    Show Versions
                    ({{ data.item.recipes_version_count }})
                  </span>
                  <span v-else>Hide Versions</span>
                </b-button>
                <b-button
                  size="sm"
                  v-wave
                  variant="outline-primary"
                  class="mr-4 align-middle"
                  :disabled="recipesResponses('manageRecipe')"
                  @click="selectRecipe(data.item), showModal('manageRecipeModal')">
                  <i class="fa fa-pen fa-sm"></i>
                  <span>Edit</span>
                </b-button>
                <b-button
                  :id="`deleteButton-${data.item._id}`"
                  v-wave
                  size="sm"
                  variant="outline-primary"
                  class="btn-icon"
                  :disabled="recipesResponses('deleteRecipe')"
                  @click="selectRecipe(data.item), showDeletePopover(data.item._id)">
                  <i class="fa fa-trash fa-sm"></i>
                </b-button>
              </div>
            </template>
            <template #row-details="row">
              <div class="px-2 pb-2">
                <b-overlay
                  :show="recipeVersionsPending(row.item._id)"
                  spinner-type="border"
                  variant="light"
                  spinner-variant="primary"
                  opacity="1"
                  rounded>
                  <b-card
                    no-body
                    no-header
                    class="card-custom card-custom card-border recipe-card"
                    :data-recipe="JSON.stringify(row.item)">
                    <b-card-body class="px-6 pb-6">
                      <b-table
                        :items="row.item.versions"
                        :fields="versionsFields"
                        :class="{ 'table-hover' : !recipeVersionsEmpty(row.item._id) }"
                        stacked="lg"
                        responsive
                        show-empty
                        thead-class="small no-events"
                        head-variant="white">
                        <template #cell(order)="row">
                          <span class="small font-weight-light">{{ row.index + 1 }}</span>
                        </template>
                        <template #cell(version)="row">
                          <div v-if="row.item.version">
                            <span class="font-size-xs">v</span>
                            <span>{{ row.item.version }}</span>
                          </div>
                          <i
                            v-else
                            class="fa fa-minus fa-xs opacity-50" />
                        </template>
                        <template #cell(default)="row">
                          <span
                            v-if="row.item.default == 'on'"
                            class="label label-sm label-success">
                            <i class="fas fa-check fa-sm text-white"></i>
                          </span>
                          <i
                            v-else
                            class="fa fa-minus fa-xs opacity-50" />
                        </template>
                        <template #cell(edit)="row">
                          <b-button
                            size="sm"
                            variant="link-primary"
                            class="mr-6 align-middle"
                            :disabled="recipesResponses('recipeVersion') || recipesResponses('manageRecipeVersion')"
                            @click="selectDOMRecipe($event), openRecipeVersion(row.item._id)">
                            <i class="fa fa-cog fa-sm"></i>
                            <span>Configure</span>
                          </b-button>
                          <b-button
                            :id="`deleteButton-${row.item._id}`"
                            size="sm"
                            variant="link-primary"
                            class="align-middle"
                            :disabled="recipesResponses('deleteRecipeVersion')"
                            @click="
                              selectDOMRecipe($event),
                              selectRecipeVersion(row.item),
                              showDeletePopover(row.item._id)
                            ">
                            <i class="fa fa-trash fa-sm"></i>
                            <span>Delete</span>
                          </b-button>
                        </template>
                        <template #empty="scope">
                          <no-data-found />
                        </template>
                      </b-table>
                      <b-button
                        size="sm"
                        v-wave
                        variant="outline-primary"
                        class="align-middle"
                        :disabled="recipesResponses('manageRecipeVersion')"
                        @click="
                          unselectRecipeVersion(),
                          selectRecipe(row.item),
                          showModal('manageRecipeVersionModal')
                        ">
                        <i class="fa fa-plus fa-sm p-0 p-sm-1"></i>
                        <span class="d-none d-sm-inline">Add New Version</span>
                      </b-button>
                    </b-card-body>
                  </b-card>
                </b-overlay>
              </div>
            </template>
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="recipe in recipesList"
      :key="`popover-${recipe._id}`"
      :target="`deleteButton-${recipe._id}`"
      :ref="`popover-${recipe._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete «<span class="font-weight-bolder">{{ recipe.name }}</span>» recipe?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(recipe._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(), hideDeletePopover(recipe._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <b-popover
      v-for="version in openedRecipeVersions"
      :key="`popover-${version._id}`"
      :target="`deleteButton-${version._id}`"
      :ref="`popover-${version._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete script «<span class="font-weight-bolder"><span class="font-size-xs">v</span>{{ version.version }}</span>»?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(version._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmVersionDelete(), hideDeletePopover(version._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <b-modal
      ref="manageRecipeModal"
      centered
      size="lg"
      :title="manageRecipeModalTitle"
      hide-header-close
      scrollable
      footer-bg-variant=""
      no-close-on-esc
      @hide="onHideAttempt"
      @hidden="unselectRecipe(), clearErrors()">
      <b-form @submit.stop.prevent>
        <b-form-group
          id="name-recipe"
          label="Name"
          label-for="name-recipe">
          <b-form-input
            id="name-recipe"
            name="name-recipe"
            v-model="selectedRecipe.name"
            :state="getValidationState('manageRecipeErrors', 'name')"
            aria-describedby="name-feedback"
            placeholder="Open VPN Installation" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageRecipeError('name')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="slug-recipe"
          label="Slug"
          label-for="slug-recipe">
          <b-form-input
            id="slug-recipe"
            name="slug-recipe"
            v-model="selectedRecipe.slug"
            :state="getValidationState('manageRecipeErrors', 'slug')"
            aria-describedby="slug-feedback"
            placeholder="OPENVPN_INSTALL"
            v-uppercase />
          <b-form-invalid-feedback
            v-for="(error, index) in manageRecipeError('slug')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="vcs_src_url-recipe"
          label="Source VCS URL (optional)"
          label-for="vcs_src_url-recipe">
          <b-form-input
            id="vcs_src_url-recipe"
            name="vcs_src_url-recipe"
            v-model="selectedRecipe.vcs_src_url"
            :state="getValidationState('manageRecipeErrors', 'vcs_src_url')"
            aria-describedby="vcs_src_url-feedback"
            placeholder="https://git.company.com/HT/vpn_client/file.vcs" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageRecipeError('vcs_src_url')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="tags-recipe"
          label="Tags (optional)"
          label-for="tags-recipe">
          <b-form-tags
            id="tags-recipe"
            name="tags-recipe"
            tag-variant="light-primary"
            v-model="selectedRecipe.tags"
            separator=" "
            :state="getValidationState('manageRecipeErrors', 'tags')"
            aria-describedby="tags-feedback"
            :placeholder="selectedRecipe.tags.length == 0 ? 'Enter tags separated by space' : ''"
            no-add-on-enter
            remove-on-delete />
          <b-form-invalid-feedback
            v-for="(error, index) in manageRecipeError('tags')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="description-recipe"
          label="Description (optional)"
          label-for="description-recipe"
          :class="selectedRecipeExist ? 'mb-0' : 'mb-8'">
          <b-form-textarea
            id="description-recipe"
            name="description-recipe"
            v-model="selectedRecipe.description"
            rows="2"
            no-resize
            :state="getValidationState('manageRecipeErrors', 'description')"
            aria-describedby="description-feedback"
            placeholder="This recipe will help you to install Open VPN" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageRecipeError('description')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          v-if="!selectedRecipeExist"
          class="mb-0">
          <b-row>
            <b-col
              col
              lg="4"
              md="4"
              sm="3"
              cols="3"
              class="pr-2 pr-md-4">
              <b-form-group
                id="version-recipe"
                label="Version (optional)"
                label-for="version-recipe"
                class="w-100">
                <b-form-input
                  v-model="selectedRecipe.version"
                  placeholder="1.00" />
              </b-form-group>
            </b-col>
            <b-col
              col
              lg="8"
              md="8"
              sm="9"
              cols="9"
              class="d-flex pl-2 pl-md-4">
              <b-form-group
                id="source-recipe"
                label="Source Code"
                label-for="source-recipe"
                class="w-100 mb-0">
                <b-form-textarea
                  v-model="selectedRecipe.source"
                  rows="6"
                  no-resize
                  :placeholder="sourceCodePlaceholder" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <div class="d-flex w-100">
          <b-button
            v-b-tooltip.hover.right
            ref="cancelModal"
            title="Are you sure? Unsaved data will be lost."
            variant="outline-secondary"
            size="md"
            @click="hideModal('manageRecipeModal')">
            Cancel
          </b-button>
          <b-button
            variant="primary"
            v-wave
            size="md"
            class="ml-auto"
            @click="submitRecipe()">
            Submit
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      ref="manageRecipeVersionModal"
      centered
      size="xl"
      :title="manageRecipeVersionModalTitle"
      hide-header-close
      scrollable
      footer-bg-variant=""
      no-close-on-esc
      @hide="onHideAttempt"
      @hidden="
        unselectRecipe(),
        clearRecipeVersion(),
        clearErrors()
      ">
      <b-form @submit.stop.prevent>
        <b-form-group class="mb-0">
          <b-row>
            <b-col
              col
              lg="3"
              md="3"
              sm="3"
              cols="3"
              class="pr-2 pr-md-4">
              <b-form-group
                id="version-recipe"
                label="Version"
                label-for="version-recipe"
                class="w-100 mb-2">
                <b-form-input
                  id="version-recipe"
                  name="version-recipe"
                  v-model="selectedRecipeVersion.version"
                  :state="getValidationState('manageRecipeVersionErrors', 'version')"
                  aria-describedby="version-feedback"
                  placeholder="1.00" />
                <b-form-invalid-feedback
                  v-for="(error, index) in manageRecipeVersionError('version')"
                  :key="`${error.field}-${index}`">
                  {{ error.message }}
                </b-form-invalid-feedback>
              </b-form-group>
              <b-form-group class="mb-0">
                <b-form-checkbox
                  id="default-checkbox"
                  v-model="selectedRecipeVersion.default"
                  :disabled="defaultCheckboxDisabled(selectedRecipe._id)"
                  name="default-checkbox">
                  Use as default
                  <i
                    v-if="defaultCheckboxDisabled(selectedRecipe._id)"
                    v-b-tooltip.hover.top
                    title="At least one version must be default"
                    class="fa fa-question-circle fa-sm" />
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col
              col
              lg="9"
              md="9"
              sm="9"
              cols="9"
              class="d-flex pl-2 pl-md-4">
              <b-form-group
                id="source-recipe"
                label="Source Code"
                label-for="source-recipe"
                class="w-100 mb-0">
                <b-form-textarea
                  id="source-recipe"
                  name="source-recipe"
                  v-model="selectedRecipeVersion.source"
                  rows="6"
                  resize
                  :state="getValidationState('manageRecipeVersionErrors', 'source')"
                  aria-describedby="source-feedback"
                  :placeholder="sourceCodePlaceholder" />
                <b-form-invalid-feedback
                  v-for="(error, index) in manageRecipeVersionError('source')"
                  :key="`${error.field}-${index}`">
                  {{ error.message }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <div class="d-flex w-100">
          <b-button
            v-b-tooltip.hover.right
            ref="cancelModal"
            title="Are you sure? Unsaved data will be lost."
            variant="outline-secondary"
            size="md"
            @click="hideModal('manageRecipeVersionModal')">
            Cancel
          </b-button>
          <b-button
            variant="primary"
            v-wave
            size="md"
            class="ml-auto"
            @click="submitRecipeVersion()">
            Submit
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  // actions
  GET_RECIPES,
  MANAGE_RECIPE,
  DELETE_RECIPE,
  GET_RECIPE_VERSIONS,
  GET_RECIPE_VERSION,
  MANAGE_RECIPE_VERSION,
  DELETE_RECIPE_VERSION,

  // mutations
  SELECT_RECIPE,
  UNSELECT_RECIPE,
  CLEAR_RECIPES,
  CLEAR_MANAGE_RECIPE_ERRORS,
  CLEAR_MANAGE_RECIPE_VERSION_ERRORS,
  SAVE_RECIPE_VERSION,
  CLEAR_RECIPE_VERSION,
} from '@/core/services/store/recipes.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { toastOptions } from '@/main'

export default {
  name: 'SettingsRecipes',
  directives: {
    uppercase: function(el) {
      el.value = el.value.toUpperCase()
    }
  },
  data() {
    return {
      recipesFields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold', 'text-nowrap'],
          thStyle: 'width: 20%;'
        },
        {
          label: 'Slug',
          key: 'slug',
          class: ['align-middle', 'text-nowrap', 'font-size-sm'],
        },
        {
          label: 'Tags',
          key: 'tags',
          class: ['align-middle', 'text-nowrap', 'font-size-sm'],
          thStyle: 'width: 250px;'
        },
        {
          label: 'Groups',
          key: 'groups',
          class: ['align-middle', 'text-nowrap', 'font-size-sm'],
        },
        {
          label: 'Default Version',
          key: 'version',
          class: ['align-middle', 'text-nowrap'],
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5'],
        }
      ],
      versionsFields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 80px;'
        },
        {
          label: 'Script Version',
          key: 'version',
          class: ['align-middle', 'text-nowrap'],
        },
        {
          label: 'Size',
          key: 'size_unit',
          class: ['align-middle', 'text-nowrap'],
        },
        {
          label: 'Default',
          key: 'default',
          class: ['align-middle', 'text-nowrap'],
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-lg-right', 'text-nowrap'],
        }
      ],
    }
  },
  computed: {
    ...mapGetters([
      'recipesList',
      'recipesEmpty',
      'totalRecipes',
      'recipeVersionsEmpty',
      'defaultCheckboxDisabled',
      'recipeTagsEmpty',
      'recipeVersionsPending',
      'selectedRecipe',
      'selectedRecipeExist',
      'selectedRecipeVersion',
      'manageRecipeModalTitle',
      'manageRecipeVersionModalTitle',
      'selectedRecipeVersionExist',
      'openedRecipeVersions',
      'manageRecipeErrors',
      'manageRecipeError',
      'manageRecipeVersionErrors',
      'manageRecipeVersionError',
      'recipeGroupsEmpty',
      'recipeHasDescription',
      'sourceCodePlaceholder',
      'recipesResponses',
    ]),
  },
  created() {
    this.$store.dispatch(GET_RECIPES)

    eventBus.$on('showToast/Recipes', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    this.$store.commit(CLEAR_RECIPES)

    eventBus.$off('showToast/Recipes')
  },
  methods: {
    selectRecipe(recipe) {
      this.$store.commit(SELECT_RECIPE, recipe)
    },

    unselectRecipe() {
      this.$store.commit(UNSELECT_RECIPE)
    },

    selectDOMRecipe(event) {
      // I had to do this workaround with 'data-recipe' attribute on parent <b-card>
      // because I needed to somehow save parent tr data inside child table
      // <b-table> receives only .versions of parent item (:items="row.item.versions") but I needed data from WHOLE recipe item
      // see => https://bootstrap-vue.org/docs/components/table#row-details-support
      this.selectRecipe(JSON.parse(event.target.closest('[data-recipe]').dataset.recipe))
    },

    selectRecipeVersion(version) {
      this.$store.commit(SAVE_RECIPE_VERSION, version)
    },

    unselectRecipeVersion() {
      this.$store.commit(CLEAR_RECIPE_VERSION)
    },

    getRecipeVersions(id, isShowing) {
      this.$store.dispatch(GET_RECIPE_VERSIONS, {
        id: id,
        isShowing: isShowing,
      })
    },

    openRecipeVersion(id) {
      this.$store.dispatch(GET_RECIPE_VERSION, id)
        .then(() => {
          this.showModal('manageRecipeVersionModal')
        })
    },

    clearRecipeVersion() {
      this.$store.commit(CLEAR_RECIPE_VERSION)
    },

    showModal(ref) {
      this.$refs[ref].show()
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    onHideAttempt(event) {
      const button = this.$refs['cancelModal']
      if (event.trigger == 'backdrop') {
        event.preventDefault()
        button.classList.add('btn-shake')
        setTimeout(() => { button.classList.remove('btn-shake') }, 500)
      }
    },

    getValidationState(getter, field) {
      return this[getter].some(err => err.field == field) ? false : null
    },

    clearErrors() {
      this.$store.commit(CLEAR_MANAGE_RECIPE_ERRORS)
      this.$store.commit(CLEAR_MANAGE_RECIPE_VERSION_ERRORS)
    },

    submitRecipe() {
      this.$store.dispatch(MANAGE_RECIPE)
        .then(() => {
          this.hideModal('manageRecipeModal')
        })
    },

    submitRecipeVersion() {
      this.$store.dispatch(MANAGE_RECIPE_VERSION)
        .then(() => {
          this.hideModal('manageRecipeVersionModal')
        })
    },

    showDeletePopover(id) {
      this.$root.$emit('bv::hide::popover')
      this.$root.$emit('bv::show::popover', `deleteButton-${id}`)
    },

    hideDeletePopover(id) {
      this.$root.$emit('bv::hide::popover', `deleteButton-${id}`)
    },

    confirmDelete() {
      this.$store.dispatch(DELETE_RECIPE)
    },

    confirmVersionDelete() {
      this.$store.dispatch(DELETE_RECIPE_VERSION)
    },

    showToast(message, variant) {
      this.$bvToast.toast(message, {
        variant: variant,
        ...toastOptions
      })
    }
  }
}
</script>
